import { createContext, useContext, useEffect, useMemo, useState } from "react";

// Bridges a route (e.g. a locked lesson) to the global <DrawingLayer>: a page can
// suppress the annotation layer when its real content isn't on screen. Without
// this, ink anchored to lesson content would render over the paywall — or leak
// the owner's notes on content the visitor can't see.
//
// It also carries the reverse signal. <DrawingLayer> publishes whether the page
// currently needs blank space to draw on, so a page can reserve it: see
// <LessonVideoSlot>, which used to reserve the video frame on every lesson and
// so showed ~400 un-recorded lessons a large empty box.
interface DrawingValue {
  suppressed: boolean;
  setSuppressed: (v: boolean) => void;
  canvasReserved: boolean;
  setCanvasReserved: (v: boolean) => void;
}

const DrawingContext = createContext<DrawingValue | null>(null);

export function DrawingSuppressProvider({ children }: { children: React.ReactNode }) {
  const [suppressed, setSuppressed] = useState(false);
  const [canvasReserved, setCanvasReserved] = useState(false);
  const value = useMemo(
    () => ({ suppressed, setSuppressed, canvasReserved, setCanvasReserved }),
    [suppressed, canvasReserved],
  );
  return <DrawingContext.Provider value={value}>{children}</DrawingContext.Provider>;
}

// Read side, used by <DrawingLayer>.
export function useDrawingSuppressed(): boolean {
  return useContext(DrawingContext)?.suppressed ?? false;
}

// Write side, used by a page: suppress the layer while `active` is true, and
// release it on unmount so navigating away restores drawings.
export function useSuppressDrawing(active: boolean): void {
  const set = useContext(DrawingContext)?.setSuppressed;
  useEffect(() => {
    if (!set) return;
    set(active);
    return () => set(false);
  }, [active, set]);
}

// True while the owner is drawing, or while this page already carries saved ink.
// A page reserving blank space for the canvas renders it only when this is true,
// so a visitor never sees space kept for a drawing that isn't there. Existing
// drawings still keep their space, or the ink would land on the shifted content.
export function useCanvasReserved(): boolean {
  return useContext(DrawingContext)?.canvasReserved ?? false;
}

// Write side, used by <DrawingLayer>.
export function usePublishCanvasReserved(reserved: boolean): void {
  const set = useContext(DrawingContext)?.setCanvasReserved;
  useEffect(() => {
    if (!set) return;
    set(reserved);
    return () => set(false);
  }, [reserved, set]);
}
